import { useState } from 'react';

import { auth } from '../firebase';
import {
  GOOGLE_PROVIDER,
  PASSWORD_PROVIDER,
  addPassword,
  connectedProviders,
  connectGoogle,
} from '../lib/authLink';
import { errorCode } from '../lib/errors';
import Field from './Field';

/** Firebase's shortest accepted password. */
const MIN_PASSWORD = 6;

/**
 * Which ways you can sign in, and how to add the other one.
 *
 * The linking prompt on the sign-in screen catches the person who walks into
 * the collision. This is for the person who goes looking — "I want to use
 * Google from now on" is a thought you have while logged in, not while staring
 * at a login form.
 *
 * There is deliberately no way to *remove* a method. Both signing you into the
 * same account costs nothing, while an unlink is one misunderstanding away from
 * a player locking themselves out of an account holding every pun they have
 * ever written.
 */
function SignInMethods() {
  // Read straight from Auth rather than from app state: providerData is the
  // only place this lives, and it is not part of the Firestore profile.
  const user = auth.currentUser;

  const [providers, setProviders] = useState<string[]>(user ? connectedProviders(user) : []);
  const [busy, setBusy] = useState<'google' | 'password' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  if (!user) return null;

  const hasGoogle = providers.includes(GOOGLE_PROVIDER);
  const hasPassword = providers.includes(PASSWORD_PROVIDER);

  const report = (error: unknown, fallback: string) => {
    console.error('[SignInMethods]', error);
    const code = errorCode(error);

    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return;

    if (code === 'auth/credential-already-in-use') {
      setProblem('That Google account is already connected to a different Pun and Done account.');
    } else if (code === 'auth/requires-recent-login') {
      // Firebase refuses security-relevant changes on a stale session.
      setProblem('For safety this needs a fresh sign-in. Sign out, sign back in, and try again.');
    } else if (code === 'auth/email-already-in-use') {
      setProblem('That email already has a password on another account.');
    } else if (code === 'auth/weak-password') {
      setProblem(`Passwords need at least ${MIN_PASSWORD} characters.`);
    } else {
      setProblem(fallback);
    }
  };

  const handleConnectGoogle = async () => {
    setBusy('google');
    setProblem(null);
    setDone(null);

    try {
      await connectGoogle(user);
      setProviders(connectedProviders(user));
      setDone('Google is connected. Either method signs you in now.');
    } catch (error) {
      report(error, 'Could not connect Google. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const handleAddPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy('password');
    setProblem(null);
    setDone(null);

    try {
      await addPassword(user, password);
      setProviders(connectedProviders(user));
      setPassword('');
      setShowPasswordForm(false);
      setDone('Password set. You can now sign in with either method.');
    } catch (error) {
      report(error, 'Could not set a password. Try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mt-5 rounded-2xl border-2 border-ink bg-white px-5 py-5 shadow-pop">
      <h2 className="font-display font-bold">How you sign in</h2>
      <p className="mb-4 mt-1 text-sm text-ink/55">
        Connect both and either one gets you into this same account.
      </p>

      {problem && (
        <p className="mb-3 rounded-xl border-2 border-ink bg-coral px-4 py-2.5 text-sm font-medium text-white">
          {problem}
        </p>
      )}
      {done && (
        <p className="mb-3 rounded-xl border-2 border-ink bg-lagoon px-4 py-2.5 text-sm font-medium text-white">
          {done}
        </p>
      )}

      <ul className="space-y-2.5">
        <Method
          label="Email and password"
          detail={hasPassword ? user.email ?? 'Connected' : 'Not set up'}
          connected={hasPassword}
          action={
            hasPassword ? null : (
              <button
                type="button"
                onClick={() => setShowPasswordForm((open) => !open)}
                className="press rounded-full border-2 border-ink bg-white px-4 py-1.5 font-display text-xs font-bold shadow-pop-sm"
              >
                {showPasswordForm ? 'Cancel' : 'Set a password'}
              </button>
            )
          }
        />

        {!hasPassword && showPasswordForm && (
          <li>
            <form
              onSubmit={handleAddPassword}
              className="rounded-xl border-2 border-ink bg-paper px-4 py-4"
            >
              <Field
                label="New password"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
                minLength={MIN_PASSWORD}
                required
              />
              <button
                type="submit"
                disabled={busy === 'password' || password.length < MIN_PASSWORD}
                className="press mt-3 w-full rounded-xl border-2 border-ink bg-mustard px-4 py-2.5 font-display font-bold shadow-pop-sm disabled:cursor-not-allowed disabled:bg-ink/10 disabled:text-ink/30"
              >
                {busy === 'password' ? 'Saving...' : 'Set password'}
              </button>
              <p className="mt-2 text-xs text-ink/50">
                Useful as a backup if you ever lose access to the Google account.
              </p>
            </form>
          </li>
        )}

        <Method
          label="Google"
          detail={hasGoogle ? 'Connected' : 'Not connected'}
          connected={hasGoogle}
          action={
            hasGoogle ? null : (
              <button
                type="button"
                onClick={handleConnectGoogle}
                disabled={busy === 'google'}
                className="press rounded-full border-2 border-ink bg-white px-4 py-1.5 font-display text-xs font-bold shadow-pop-sm disabled:cursor-not-allowed disabled:text-ink/30"
              >
                {busy === 'google' ? 'Opening...' : 'Connect'}
              </button>
            )
          }
        />
      </ul>
    </section>
  );
}

function Method({
  label,
  detail,
  connected,
  action,
}: {
  label: string;
  detail: string;
  connected: boolean;
  action: React.ReactNode;
}) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-xl border-2 border-ink bg-paper px-4 py-3">
      <span className="min-w-0">
        <span className="block font-display font-bold">
          {connected && (
            <span className="text-lagoon" aria-hidden="true">
              &#10003;{' '}
            </span>
          )}
          {label}
        </span>
        <span className="mt-0.5 block truncate text-sm text-ink/55">{detail}</span>
      </span>
      {action}
    </li>
  );
}

export default SignInMethods;
